import { useState, useEffect } from 'react';
import { Card, Button, Textarea, Badge, Select } from '@lib/ui';
import { useAuthContext } from '@lib/auth';
import { useLocalStorage } from '@lib/hooks';

const SparklesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z"
    />
  </svg>
);

const CopyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
    />
  </svg>
);

const RefreshIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

const LightbulbIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
    />
  </svg>
);

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const LANGUAGE_OPTIONS = [
  { value: 'hebrew', label: 'Hebrew' },
  { value: 'english', label: 'English' },
];

const DEFAULT_PERSONA_1 = `# Persona: Supplement Enthusiast

## Basic Info
- **Name**: Mia
- **Age**: 23
- **Location**: Los Angeles, CA
- **Occupation**: Yoga instructor and wellness content creator

## Personality Traits
- Energetic and bubbly
- Passionate about holistic health
- Always researching the latest wellness trends
- Loves sharing discoveries with friends
- Uses lots of emojis and exclamation marks

## Health Interests
- Adaptogens (ashwagandha, lion's mane, reishi)
- Collagen peptides for skin health
- Probiotics and gut health
- Plant-based protein powders
- Vitamin D and magnesium supplements
- Beauty-from-within supplements (biotin, hyaluronic acid)

## Speaking Style
- Casual and conversational
- Uses phrases like "obsessed with", "game-changer", "literally changed my life"
- Includes personal anecdotes
- Asks rhetorical questions to engage readers
- Often mentions morning routines and wellness rituals

## Example Phrases
- "Okay so you HAVE to hear about this..."
- "I've been taking this for two weeks and omg..."
- "My skin has never looked better"
- "This is now part of my non-negotiable morning routine"
- "Link in bio for anyone who wants to try!"`;

const DEFAULT_PERSONA_2 = `# Persona: Fitness Enthusiast

## Basic Info
- **Name**: Jake
- **Age**: 27
- **Location**: Miami, FL
- **Occupation**: Personal trainer and fitness influencer

## Personality Traits
- Confident and motivational
- Obsessed with gains and optimization
- Always tracking macros and performance
- Loves sharing workout tips and supplement stacks
- Uses bro-speak and gym terminology

## Health Interests
- Protein powders and mass gainers
- Pre-workout supplements
- Creatine and BCAAs
- Testosterone boosters
- Recovery supplements (glutamine, ZMA)
- Electrolytes and hydration

## Speaking Style
- Direct and enthusiastic
- Uses phrases like "bro", "gains", "beast mode", "let's go"
- Talks about PRs, reps, and sets
- References the gym lifestyle constantly
- Uses fire emojis and flexing emojis

## Example Phrases
- "Alright guys, let me put you on to this..."
- "This stuff is LEGIT, no cap"
- "Been running this for 4 weeks and the gains are insane"
- "If you're serious about your fitness goals, you need this"
- "Link in bio, thank me later"
- "Trust the process, trust the gains"`;

interface PlaygroundResponse {
  caption: string;
  hashtags: string[];
  product_url: string;
  product_name?: string;
  cached: boolean;
  system_prompt: string;
  user_prompt: string;
}

interface ImprovePersonaResponse {
  improved_persona: string;
  changes_summary: string;
}

interface CachedProduct {
  url: string;
  cache_id: string;
}

export default function Playground() {
  const { getAccessToken } = useAuthContext();
  const [persona1Text, setPersona1Text] = useLocalStorage<string>(
    'playground-persona1',
    DEFAULT_PERSONA_1
  );
  const [persona2Text, setPersona2Text] = useLocalStorage<string>(
    'playground-persona2',
    DEFAULT_PERSONA_2
  );
  const [selectedPersona, setSelectedPersona] = useState<'persona1' | 'persona2'>('persona1');
  const [outputFormat, setOutputFormat] = useState<string>('whatsapp');
  const [language, setLanguage] = useState<string>('hebrew');
  const [productUrls, setProductUrls] = useState<string>('');
  const [cachedProducts, setCachedProducts] = useState<CachedProduct[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [outputs, setOutputs] = useState<PlaygroundResponse[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Improve Persona state
  const [outputFeedback, setOutputFeedback] = useState('');
  const [isImproving, setIsImproving] = useState(false);
  const [improvedPersona, setImprovedPersona] = useState<ImprovePersonaResponse | null>(null);

  // Fetch cached products on mount
  useEffect(() => {
    const fetchCachedProducts = async () => {
      try {
        const token = await getAccessToken();
        const response = await fetch(`${API_BASE}/poc/cached-products`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setCachedProducts(data);
        }
      } catch (err) {
        console.error('Failed to fetch cached products:', err);
      }
    };

    fetchCachedProducts();
  }, [getAccessToken]);

  const getActivePersonaText = () => {
    return selectedPersona === 'persona1' ? persona1Text : persona2Text;
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setOutputs([]);

    try {
      const token = await getAccessToken();

      // Parse URLs - one per line, filter empty lines
      const urls = productUrls
        .split('\n')
        .map((url) => url.trim())
        .filter((url) => url.length > 0);

      // If no URLs provided, generate for one random product
      const urlsToProcess = urls.length > 0 ? urls : [null];

      const results: PlaygroundResponse[] = [];

      for (const url of urlsToProcess) {
        const response = await fetch(`${API_BASE}/poc/playground`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            persona: getActivePersonaText(),
            output_format: outputFormat,
            language: language,
            product_url: url,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Request failed: ${response.status}`);
        }

        const data: PlaygroundResponse = await response.json();
        results.push(data);
        // Update UI progressively
        setOutputs([...results]);
      }

      // Refresh cached products list
      const cachedResponse = await fetch(`${API_BASE}/poc/cached-products`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (cachedResponse.ok) {
        setCachedProducts(await cachedResponse.json());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImprovePersona = async () => {
    if (!outputFeedback.trim()) return;

    setIsImproving(true);
    setImprovedPersona(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/poc/improve-persona`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_persona: getActivePersonaText(),
          feedback: outputFeedback,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Request failed: ${response.status}`);
      }

      const data: ImprovePersonaResponse = await response.json();
      setImprovedPersona(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsImproving(false);
    }
  };

  const handleAcceptImprovedPersona = () => {
    if (!improvedPersona) return;

    if (selectedPersona === 'persona1') {
      setPersona1Text(improvedPersona.improved_persona);
    } else {
      setPersona2Text(improvedPersona.improved_persona);
    }

    // Reset
    setOutputFeedback('');
    setImprovedPersona(null);
  };

  const handleRejectImprovedPersona = () => {
    setImprovedPersona(null);
  };

  const handleCopy = (output: PlaygroundResponse) => {
    const text = `${output.caption}\n\n${output.hashtags.map((h) => `#${h}`).join(' ')}\n\n${output.product_url}`;
    navigator.clipboard.writeText(text);
  };

  const handleCopyCaption = (output: PlaygroundResponse) => {
    navigator.clipboard.writeText(output.caption);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">POC Playground</h1>
        <p className="mt-1 text-gray-500 dark:text-dark-300">
          Experiment with persona-based content generation for iHerb products.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Input Panel */}
        <div className="space-y-6">
          {/* Personas - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Persona 1: Supplement Girl */}
            <Card
              className={`p-4 cursor-pointer transition-all ${
                selectedPersona === 'persona1'
                  ? 'ring-2 ring-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-dark-700'
              }`}
              onClick={() => setSelectedPersona('persona1')}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 dark:text-white">Supplement Girl</h3>
                <input
                  type="radio"
                  name="persona"
                  checked={selectedPersona === 'persona1'}
                  onChange={() => setSelectedPersona('persona1')}
                  className="w-4 h-4 text-primary-500"
                />
              </div>
              <Textarea
                value={persona1Text}
                onChange={(e) => {
                  e.stopPropagation();
                  setPersona1Text(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Persona definition..."
                className="min-h-[400px] font-mono text-xs"
              />
            </Card>

            {/* Persona 2: Fitness Bro */}
            <Card
              className={`p-4 cursor-pointer transition-all ${
                selectedPersona === 'persona2'
                  ? 'ring-2 ring-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-dark-700'
              }`}
              onClick={() => setSelectedPersona('persona2')}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 dark:text-white">Fitness Bro</h3>
                <input
                  type="radio"
                  name="persona"
                  checked={selectedPersona === 'persona2'}
                  onChange={() => setSelectedPersona('persona2')}
                  className="w-4 h-4 text-primary-500"
                />
              </div>
              <Textarea
                value={persona2Text}
                onChange={(e) => {
                  e.stopPropagation();
                  setPersona2Text(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Persona definition..."
                className="min-h-[400px] font-mono text-xs"
              />
            </Card>
          </div>
          <p className="text-xs text-gray-500 dark:text-dark-400 mb-4">
            Click a persona card to select it for generation. Edit the text to customize.
          </p>

          {/* Output Feedback - Inline Persona Improvement */}
          <Card className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 mb-2">
              <LightbulbIcon />
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                Improve Persona from Output Feedback
              </h3>
            </div>
            <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
              Describe issues with the generated output (e.g., "too verbose", "not casual enough")
              and we'll suggest persona changes to fix it.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={outputFeedback}
                onChange={(e) => setOutputFeedback(e.target.value)}
                placeholder="e.g., output is too long, needs more emojis, too formal..."
                className="flex-1 px-3 py-2 text-sm border border-amber-300 dark:border-amber-700 rounded-lg bg-white dark:bg-dark-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && outputFeedback.trim()) {
                    handleImprovePersona();
                  }
                }}
              />
              <Button
                onClick={handleImprovePersona}
                disabled={isImproving || !outputFeedback.trim()}
                className="whitespace-nowrap"
              >
                {isImproving ? <span className="animate-spin">⟳</span> : 'Suggest Fix'}
              </Button>
            </div>

            {/* Show improved persona suggestion */}
            {improvedPersona && (
              <div className="mt-4 p-3 bg-white dark:bg-dark-800 rounded-lg border border-amber-300 dark:border-amber-700">
                <p className="text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
                  Suggested changes:
                </p>
                <p className="text-sm text-gray-600 dark:text-dark-300 mb-3">
                  {improvedPersona.changes_summary}
                </p>
                <div className="flex gap-2">
                  <Button onClick={handleAcceptImprovedPersona} className="text-sm">
                    Apply to Persona
                  </Button>
                  <Button variant="flat" onClick={handleRejectImprovedPersona} className="text-sm">
                    Dismiss
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Output Format & Language */}
          <Card className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Output Format
                </h2>
                <div className="flex flex-wrap gap-2">
                  {['whatsapp', 'instagram', 'twitter', 'tiktok'].map((format) => (
                    <button
                      key={format}
                      onClick={() => setOutputFormat(format)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        outputFormat === format
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200 hover:bg-gray-200 dark:hover:bg-dark-600'
                      }`}
                    >
                      {format}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Language
                </h2>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  data={LANGUAGE_OPTIONS}
                />
              </div>
            </div>
          </Card>

          {/* Product URLs */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Product URLs</h2>
              {cachedProducts.length > 0 && (
                <Badge variant="soft" color="success">
                  {cachedProducts.length} cached
                </Badge>
              )}
            </div>
            <Textarea
              value={productUrls}
              onChange={(e) => setProductUrls(e.target.value)}
              placeholder="Leave empty for random product, or paste iHerb product URLs (one per line)..."
              className="font-mono text-sm"
              rows={4}
            />
            <p className="text-xs text-gray-500 dark:text-dark-400 mt-2">
              Enter one URL per line to generate content for multiple products.
            </p>
            {cachedProducts.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 dark:text-dark-400 mb-2">
                  Cached products (click to add):
                </p>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {cachedProducts.slice(0, 10).map((product) => (
                    <button
                      key={product.cache_id}
                      onClick={() =>
                        setProductUrls((prev) => (prev ? `${prev}\n${product.url}` : product.url))
                      }
                      className="px-2 py-1 text-xs bg-gray-100 dark:bg-dark-700 rounded hover:bg-gray-200 dark:hover:bg-dark-600 truncate max-w-[200px]"
                      title={product.url}
                    >
                      {product.url.split('/pr/')[1]?.split('/')[0] || 'product'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Generate Button */}
          <Button
            className="w-full"
            onClick={handleGenerate}
            disabled={isGenerating || !getActivePersonaText().trim()}
          >
            {isGenerating ? (
              <>
                <span className="animate-spin mr-2">⟳</span>
                Generating...
              </>
            ) : (
              <>
                <SparklesIcon />
                <span className="ml-2">
                  {productUrls.split('\n').filter((u) => u.trim()).length > 1
                    ? 'Generate All'
                    : 'Generate Content'}
                </span>
              </>
            )}
          </Button>

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Output Panel */}
        <div className="space-y-6">
          <Card className="p-5 h-full flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Generated Output
                {outputs.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({outputs.length} {outputs.length === 1 ? 'result' : 'results'})
                  </span>
                )}
              </h2>
            </div>

            <div className="flex-1 min-h-[500px] overflow-y-auto">
              {isGenerating && outputs.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-pulse">
                      <SparklesIcon />
                    </div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-dark-400">
                      Generating content as persona...
                    </p>
                  </div>
                </div>
              ) : outputs.length > 0 ? (
                <div className="space-y-6">
                  {outputs.map((output, outputIndex) => (
                    <Card
                      key={outputIndex}
                      className="p-4 border border-gray-200 dark:border-dark-600"
                    >
                      {/* Card Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-700 dark:text-dark-200">
                            {output.product_name ||
                              output.product_url.split('/pr/')[1]?.split('/')[0] ||
                              `Product ${outputIndex + 1}`}
                          </span>
                          <Badge variant="soft" color="info">
                            {outputFormat.toUpperCase()}
                          </Badge>
                          {output.cached && (
                            <Badge variant="soft" color="success">
                              <RefreshIcon />
                              <span className="ml-1">Cached</span>
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="flat"
                            className="text-xs"
                            onClick={() => handleCopyCaption(output)}
                          >
                            <CopyIcon />
                            <span className="ml-1">Caption</span>
                          </Button>
                          <Button
                            variant="flat"
                            className="text-xs"
                            onClick={() => handleCopy(output)}
                          >
                            <CopyIcon />
                            <span className="ml-1">All</span>
                          </Button>
                        </div>
                      </div>

                      {/* Caption */}
                      <div className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg mb-3">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-dark-400 mb-1">
                          Caption
                        </h4>
                        <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-dark-100 font-sans">
                          {output.caption}
                        </pre>
                      </div>

                      {/* Hashtags */}
                      <div className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg mb-3">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-dark-400 mb-1">
                          Hashtags
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {output.hashtags.map((tag, idx) => (
                            <Badge key={idx} variant="soft" color="primary" className="text-xs">
                              #{tag}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Product URL */}
                      <div className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg mb-3">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-dark-400 mb-1">
                          Product URL
                        </h4>
                        <a
                          href={output.product_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-600 dark:text-primary-400 hover:underline break-all"
                        >
                          {output.product_url}
                        </a>
                      </div>

                      {/* Prompts (collapsed by default) */}
                      <div className="flex gap-2">
                        <details className="flex-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs">
                          <summary className="font-medium text-blue-700 dark:text-blue-300 cursor-pointer">
                            System Prompt
                          </summary>
                          <pre className="mt-2 whitespace-pre-wrap text-blue-800 dark:text-blue-200 font-mono max-h-32 overflow-y-auto">
                            {output.system_prompt}
                          </pre>
                        </details>
                        <details className="flex-1 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg text-xs">
                          <summary className="font-medium text-green-700 dark:text-green-300 cursor-pointer">
                            User Prompt
                          </summary>
                          <pre className="mt-2 whitespace-pre-wrap text-green-800 dark:text-green-200 font-mono max-h-32 overflow-y-auto">
                            {output.user_prompt}
                          </pre>
                        </details>
                      </div>
                    </Card>
                  ))}

                  {/* Show loading indicator when generating more */}
                  {isGenerating && (
                    <div className="flex items-center justify-center p-4">
                      <span className="animate-spin mr-2">⟳</span>
                      <span className="text-sm text-gray-500 dark:text-dark-400">
                        Generating more...
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full border-2 border-dashed border-gray-200 dark:border-dark-600 rounded-lg">
                  <div className="text-center">
                    <p className="text-gray-500 dark:text-dark-400">
                      Configure your inputs and click "Generate Content"
                    </p>
                    <p className="text-sm text-gray-400 dark:text-dark-500 mt-1">
                      Output will appear here
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
